import React, { useState, useRef, useEffect } from 'react'
import { Tab, TextField, Tooltip, IconButton } from '@material-ui/core'
import { PlayCircleFilled, PlayArrow, Pause, Clear } from '@material-ui/icons'
import { Button, Tabs } from 'sputnik-ui'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { testCall, lineTest, fetchIntercomLogs } from 'store/devices/operations'
import trackGoal from 'utils/metrika'
import { analyzeTestCall } from './callAnalysis'

const fieldStyle = { marginBottom: '.625rem' }

const STATUS_COLORS = {
  success: '#4caf50',
  failure: '#f44336',
  pending: '#ff9800',
}

function CallAnalysisResult({ analysis, pollingActive, setPollingActive }) {
  const { t } = useTranslation(['devices', 'common'])
  const STATUS_LABELS = {
    success: t('common:toast.success'),
    failure: t('common:failure'),
    pending: t('common:pending'),
  }

  if (!analysis) {
    return (
      <AnalysisBox>
        <LogPlaceholder>{t('devices:params.awaiting_logs')}</LogPlaceholder>
      </AnalysisBox>
    )
  }

  const { status, requiredEvents, matchedEvents, failureLabel } = analysis

  if (status === 'success' || status === 'failure') {
    setPollingActive(false)
  }
  return (
    <AnalysisBox>
      <AnalysisStatusRow>
        <AnalysisStatusDot color={STATUS_COLORS[status]} />
        <AnalysisStatusText color={STATUS_COLORS[status]}>
          {STATUS_LABELS[status]}
          {pollingActive && status === 'pending' && ' …'}
        </AnalysisStatusText>
      </AnalysisStatusRow>
      {failureLabel && (
        <AnalysisFailureNote>
          {t(`devices:call_test:${failureLabel.replace(/\s+/g, '')}`)}
        </AnalysisFailureNote>
      )}
      <AnalysisEventGrid>
        {requiredEvents.map((ev) => {
          const found = matchedEvents.includes(ev)
          return (
            <AnalysisEventRow key={ev}>
              <AnalysisEventIcon
                color={found ? STATUS_COLORS.success : undefined}
              >
                {found ? '✓' : '○'}
              </AnalysisEventIcon>
              <AnalysisEventName>
                {t(`devices:call_test:${ev.replace('intercom.', '')}`)}
              </AnalysisEventName>
            </AnalysisEventRow>
          )
        })}
      </AnalysisEventGrid>
    </AnalysisBox>
  )
}

export function useTestingPanel({ uuidOfCurrentDevice, dispatch, loading }) {
  const { t } = useTranslation(['devices', 'common', 'people'])

  const [currentTestTab, setCurrentTestTab] = useState('analog_line')
  const [flatNum, setFlatNum] = useState('')
  const [analogLineFlatNum, setAnalogLineFlatNum] = useState('')
  const [lineTestResult, setLineTestResult] = useState(null)
  const [logs, setLogs] = useState([])
  const [callStartTime, setCallStartTime] = useState(null)
  const [testCallPending, setTestCallPending] = useState(false)
  const [testAnalogPending, setTestAnalogPending] = useState(false)
  const [testCallHistory, setTestCallHistory] = useState([])
  const [analogLineHistory, setAnalogLineHistory] = useState([])
  const [pollingActive, setPollingActive] = useState(false)
  const [testCallErr, setTestCallErr] = useState(null)
  const activeTestIdRef = useRef(null)

  useEffect(() => {
    if (!pollingActive) return undefined
    const poll = () => {
      if (document.hidden) return
      dispatch(fetchIntercomLogs(uuidOfCurrentDevice)).then((data) => {
        if (data) {
          setLogs(data)
          if (activeTestIdRef.current !== null) {
            setTestCallHistory((prev) =>
              prev.map((entry) =>
                entry.id === activeTestIdRef.current
                  ? { ...entry, logs: data }
                  : entry
              )
            )
          }
        }
      })
    }
    poll()
    const id = setInterval(poll, 5000)
    return () => clearInterval(id)
  }, [pollingActive, uuidOfCurrentDevice, dispatch])

  const handeStartTestCall = () => {
    const id = Date.now()
    const startTime = Date.now()
    activeTestIdRef.current = id
    setCallStartTime(startTime)
    setTestCallHistory((prev) => [
      { id, flatNum, logs: [], startTime },
      ...prev,
    ])
    setLogs([])
    setTestCallPending(true)
    trackGoal('control_test_call_start', {
      intercom_uuid: uuidOfCurrentDevice,
      flat_num: flatNum ? parseInt(flatNum, 10) : null,
    })
    dispatch(
      testCall(uuidOfCurrentDevice, flatNum ? parseInt(flatNum, 10) : null)
    )
      .then(() => {
        trackGoal('control_test_call_result_success', {
          intercom_uuid: uuidOfCurrentDevice,
          flat_num: flatNum ? parseInt(flatNum, 10) : null,
        })
      })
      .catch((err) => {
        setTestCallErr(err)
        trackGoal('control_test_call_result_failed', {
          intercom_uuid: uuidOfCurrentDevice,
          flat_num: flatNum ? parseInt(flatNum, 10) : null,
        })
      })
      .finally(() => {
        setTestCallPending(false)
        setPollingActive(true)
      })
  }

  const formLogDescription = (key, value) => {
    if (key === 'status') {
      return t(`devices:analogLogDesc.statuses.${value.toLowerCase()}`)
    }
    if (key === 'block_flat') {
      return value === 'yes' ? t('common:yes') : t('common:no')
    }
    if (key === 'action') return ''
    return value
  }

  const handleAnalogTest = () => {
    setTestAnalogPending(true)
    dispatch(
      lineTest(
        uuidOfCurrentDevice,
        analogLineFlatNum ? parseInt(analogLineFlatNum, 10) : null
      )
    ).then((data) => {
      setTestAnalogPending(false)
      setLineTestResult(data)
      if (data) {
        setAnalogLineHistory((prev) => [
          { id: Date.now(), flatNum: analogLineFlatNum, result: data },
          ...prev,
        ])
      }
    })
  }

  const callAnalysis =
    callStartTime !== null
      ? analyzeTestCall({ logs, flatNum, callStartTime, testCallErr })
      : null

  const testingTabs = (
    <Tabs
      onChange={(e, tab) => setCurrentTestTab(tab)}
      value={currentTestTab}
      variant="scrollable"
      scrollButtons="auto"
    >
      <Tab
        disableRipple
        label={t('devices:params.analog')}
        value="analog_line"
        style={{ whiteSpace: 'nowrap' }}
      />
      <Tab
        disableRipple
        label={t('devices:params.call_flat')}
        value="test_call"
        style={{ whiteSpace: 'nowrap' }}
      />
    </Tabs>
  )

  const testingChildren = (
    <>
      {currentTestTab === 'analog_line' && (
        <>
          <div
            style={{
              ...fieldStyle,
              color: 'textSecondary',
              whiteSpace: 'pre-line',
            }}
          >
            {t('devices:params.before_check')}
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-end',
              gap: '1rem',
              marginBottom: '1rem',
            }}
          >
            <TextField
              label={t('people:pro.appartment_num')}
              type="number"
              value={analogLineFlatNum}
              onChange={(e) => setAnalogLineFlatNum(e.target.value)}
            />
            <Tooltip title={t('devices:actions.start_test')}>
              <span>
                <Button
                  primary
                  variant="contained"
                  rounded
                  disabled={!analogLineFlatNum || testAnalogPending}
                  onClick={handleAnalogTest}
                >
                  <PlayCircleFilled fontSize="small" />
                </Button>
              </span>
            </Tooltip>
          </div>
          <HistoryStrip>
            <HistoryLabel>{t('devices:params.logs_history')}</HistoryLabel>
            {analogLineHistory.length === 0 ? (
              <HistoryEmpty style={{ padding: '0 0.25rem' }}>—</HistoryEmpty>
            ) : (
              analogLineHistory.map((entry) => (
                <HistoryChip
                  key={entry.id}
                  onClick={() => {
                    setLineTestResult(entry.result)
                    setAnalogLineFlatNum(entry.flatNum)
                  }}
                >
                  #{entry.flatNum || '—'}
                </HistoryChip>
              ))
            )}
          </HistoryStrip>
          <LogBox style={{ borderTopLeftRadius: 0, borderTopRightRadius: 0 }}>
            {lineTestResult === null ? (
              <LogPlaceholder>
                {testAnalogPending
                  ? t('devices:params.analog_testing_pending')
                  : t('devices:params.awaiting_logs')}
              </LogPlaceholder>
            ) : (
              Object.entries(lineTestResult).map(([key, value]) => (
                <LogLine key={key}>
                  <LogTime>{t(`devices:analogLogDesc.${key}`)}</LogTime>{' '}
                  <LogEvent>{formLogDescription(key, value)}</LogEvent>{' '}
                </LogLine>
              ))
            )}
          </LogBox>
        </>
      )}
      {currentTestTab === 'test_call' && (
        <>
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-end',
              gap: '1rem',
              marginBottom: '1rem',
            }}
          >
            <TextField
              label={t('people:pro.appartment_num')}
              type="number"
              value={flatNum}
              onChange={(e) => setFlatNum(e.target.value)}
            />
            <Tooltip title={t('devices:actions.start_test')}>
              <span>
                <Button
                  primary
                  variant="contained"
                  rounded
                  disabled={!flatNum || testCallPending}
                  onClick={handeStartTestCall}
                >
                  <PlayCircleFilled fontSize="small" />
                </Button>
              </span>
            </Tooltip>
          </div>
          <HistoryStrip>
            <HistoryLabel>{t('devices:params.logs_history')}</HistoryLabel>
            {testCallHistory.length === 0 ? (
              <HistoryEmpty style={{ padding: '0 0.25rem' }}>—</HistoryEmpty>
            ) : (
              testCallHistory.map((entry) => (
                <HistoryChip
                  key={entry.id}
                  onClick={() => {
                    setLogs(entry.logs)
                    setFlatNum(entry.flatNum)
                    setCallStartTime(entry.startTime)
                    setPollingActive(false)
                  }}
                >
                  #{entry.flatNum || '—'}
                </HistoryChip>
              ))
            )}
          </HistoryStrip>
          <div style={{ position: 'relative' }}>
            <LogBoxActions>
              {flatNum && <LogBoxFlatLabel>#{flatNum}</LogBoxFlatLabel>}
              <Tooltip
                title={
                  pollingActive
                    ? t('devices:params.logs_stop')
                    : t('devices:params.logs_start')
                }
              >
                <IconButton
                  size="small"
                  onClick={() => setPollingActive(!pollingActive)}
                  disabled={loading}
                >
                  {pollingActive ? (
                    <Pause fontSize="small" />
                  ) : (
                    <PlayArrow fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>
              <Tooltip title={t('devices:params.clear_logs')}>
                <IconButton
                  size="small"
                  onClick={() => {
                    setLogs([])
                    setCallStartTime(null)
                  }}
                  disabled={loading}
                >
                  <Clear fontSize="small" />
                </IconButton>
              </Tooltip>
            </LogBoxActions>
            <AnalysisWrapper>
              <CallAnalysisResult
                analysis={callAnalysis}
                pollingActive={pollingActive}
                setPollingActive={setPollingActive}
              />
            </AnalysisWrapper>
          </div>
        </>
      )}
    </>
  )

  return { testingTabs, testingChildren }
}

const HistoryStrip = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.25rem;
  overflow-x: auto;
  border: 1px solid rgba(0, 0, 0, 0.23);
  border-radius: 4px 4px 0 0;
  border-bottom: none;
  padding: 0.25rem 0.5rem;
  font-family: monospace;
  font-size: 0.8rem;
  min-height: 2rem;
  background: ${(p) => p.theme.palette.background.primary};
`

const HistoryLabel = styled.span`
  flex-shrink: 0;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${(p) => p.theme.palette.text.secondary};
  margin-right: 0.25rem;
`

const HistoryChip = styled.button`
  flex-shrink: 0;
  padding: 0.1rem 0.4rem;
  border: 1px solid rgba(0, 0, 0, 0.23);
  border-radius: 3px;
  background: none;
  cursor: pointer;
  font-family: monospace;
  font-size: 0.75rem;
  color: ${(p) => p.theme.palette.primary.main};

  &:hover {
    background: rgba(0, 0, 0, 0.06);
  }
`

const LogBoxFlatLabel = styled.span`
  font-family: monospace;
  font-size: 0.75rem;
  font-weight: 600;
  color: ${(p) => p.theme.palette.primary.main};
  align-self: center;
  margin-right: 0.25rem;
`

const LogBoxActions = styled.div`
  position: absolute;
  top: 0.25rem;
  right: 0.25rem;
  display: flex;
  gap: 0.1rem;
  z-index: 1;
`

const HistoryEmpty = styled.div`
  padding: 0.4rem 0.5rem;
  font-family: monospace;
  font-size: 0.8rem;
  color: ${(p) => p.theme.palette.text.disabled};
`

const LogBox = styled.div`
  border: 1px solid rgba(0, 0, 0, 0.23);
  border-radius: 4px;
  padding: 0.75rem;
  min-height: 180px;
  max-height: 400px;
  overflow-y: auto;
  font-family: monospace;
  font-size: 0.8rem;
  background: ${(p) => p.theme.palette.background.primary};
`

const LogLine = styled.div`
  margin-bottom: 0.5rem;
  white-space: pre-wrap;
  word-break: break-all;
`

const LogTime = styled.span`
  color: ${(p) => p.theme.palette.text.secondary};
`

const LogEvent = styled.span`
  color: ${(p) => p.theme.palette.primary.main};
  font-weight: 500;
`

const LogPlaceholder = styled.span`
  color: ${(p) => p.theme.palette.text.disabled};
`

const AnalysisWrapper = styled.div`
  border-radius: 0 0 4px 4px;
`

const AnalysisBox = styled.div`
  border: 1px solid rgba(0, 0, 0, 0.23);
  border-radius: 4px;
  padding: 0.75rem;
  min-height: 180px;
  font-family: monospace;
  font-size: 0.8rem;
  background: ${(p) => p.theme.palette.background.primary};
`

const AnalysisStatusRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
`

const AnalysisStatusDot = styled.span`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${(p) => p.color || p.theme.palette.text.disabled};
`

const AnalysisStatusText = styled.span`
  font-weight: 600;
  font-size: 0.85rem;
  color: ${(p) => p.color || p.theme.palette.text.primary};
`

const AnalysisFailureNote = styled.div`
  font-size: 0.8rem;
  color: #f44336;
  margin-bottom: 0.5rem;
`

const AnalysisEventGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
`

const AnalysisEventRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
`

const AnalysisEventIcon = styled.span`
  font-size: 0.9rem;
  width: 1rem;
  flex-shrink: 0;
  color: ${(p) => p.color || p.theme.palette.text.disabled};
`

const AnalysisEventName = styled.span`
  color: ${(p) => p.theme.palette.text.secondary};
`
